/**
 * <admira-deadline> — a sign-up deadline above a form: the date, a countdown,
 * and a second message once the date has passed.
 *
 * The section renders the state it sees at render time (`data-state`), but
 * Shopify serves pages from cache, so the browser's clock decides: this element
 * re-reads the deadline on connect, every minute, and exactly at the deadline.
 *
 * It also keeps any `[data-deadline-program]` input in the page in step —
 * `data-value-open` before the deadline, `data-value-closed` after — so the
 * request says which programme it was sent under. The time on the email Shopify
 * sends remains the record; this is a convenience for whoever reads it.
 *
 * Wording comes from data attributes (locale strings); unit names are picked
 * with `Intl.PluralRules('ro')` — 1 zi, 2 zile, 20 de zile.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

class AdmiraDeadline extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  /** @type {Record<string, Record<string, string>>} */
  #units = {};
  #plural = new Intl.PluralRules('ro');

  connectedCallback() {
    try {
      this.#units = JSON.parse(this.dataset.units ?? '{}');
    } catch {
      this.#units = {};
    }
    this.#tick();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #tick = () => {
    const deadline = Date.parse(this.dataset.deadline ?? '');
    if (Number.isNaN(deadline)) return;

    const left = deadline - Date.now();
    const open = left > 0;
    this.#render(open, left);

    if (!open) return;
    // Wake on the next whole minute, or at the deadline if that comes first.
    this.#timer = window.setTimeout(this.#tick, Math.min(left, MINUTE - (Date.now() % MINUTE) + 50));
  };

  /**
   * @param {boolean} open
   * @param {number} left - milliseconds to the deadline
   */
  #render(open, left) {
    this.dataset.state = open ? 'open' : 'closed';
    for (const part of this.querySelectorAll('[data-deadline-when]')) {
      if (part instanceof HTMLElement) part.hidden = part.dataset.deadlineWhen !== this.dataset.state;
    }

    const countdown = this.querySelector('[data-deadline-countdown]');
    if (countdown instanceof HTMLElement) {
      countdown.textContent = open ? this.#format(left) : '';
      countdown.hidden = !open;
    }

    for (const input of document.querySelectorAll('[data-deadline-program]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.value = (open ? input.dataset.valueOpen : input.dataset.valueClosed) ?? input.value;
    }
  }

  /** @param {number} left */
  #format(left) {
    const days = Math.floor(left / DAY);
    const hours = Math.floor((left % DAY) / HOUR);
    const minutes = Math.max(1, Math.ceil((left % HOUR) / MINUTE));

    let parts;
    if (days > 0) parts = [this.#unit(days, 'day'), hours > 0 ? this.#unit(hours, 'hour') : ''];
    else if (hours > 0) parts = [this.#unit(hours, 'hour'), this.#unit(minutes % 60, 'minute')];
    else parts = [this.#unit(minutes, 'minute')];

    const value = parts.filter(Boolean).join(` ${this.dataset.and ?? 'și'} `);
    return (this.dataset.countdown ?? '{{ time }}').replace('{{ time }}', value);
  }

  /**
   * @param {number} count
   * @param {'day' | 'hour' | 'minute'} unit
   */
  #unit(count, unit) {
    if (count <= 0) return '';
    const forms = this.#units[unit] ?? {};
    const word = forms[this.#plural.select(count)] ?? forms.other ?? '';
    return `${count} ${word}`.trim();
  }
}

if (!customElements.get('admira-deadline')) {
  customElements.define('admira-deadline', AdmiraDeadline);
}
